import React, { useEffect, useState } from 'react';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import { Stack, Typography, Chip, IconButton, Paper, Box, LinearProgress, Tooltip } from '@mui/material';
import { useTBContext } from '../Contexts/useTBStates';
import { formatDateForDisplay, isEventPast } from '@moment-golf/shared';
import { SepiaChip } from '../../shared/Components/SepiaChip';
import { AutoFitText } from '../../shared/Components/AutoFitText';
import { EventPublishStatusIcon } from './EventPublishStatusIcon';
import {
    getEventFillRate,
    getEventPublishIconKind,
    getEventStatusChipSx,
    getEventStatusLabel,
} from '../utils/eventCardStatus';

type EventCardEvent = {
    _id: string;
    eventName: string;
    courseName: string;
    startDate: string;
    endDate: string;
    totalRounds: number;
    totalHoles: number;
    published: boolean;
    canceled: boolean;
    playerCap?: number;
    playType?: string | null;
    privacyRegType?: string | null;
    eventCardImageURL?: string | null;
    registrationCount?: number;
};

/** Extracted so each card gets its own props and avoids stale closure / key reuse issues */
const EventCard = React.memo(({ event, factor, onSelect }: {
    event: EventCardEvent;
    factor: number;
    onSelect: () => void;
}) => {
    const [logoFailed, setLogoFailed] = useState(false);
    useEffect(() => {
        setLogoFailed(false);
    }, [event.eventCardImageURL]);
    const opts = { year: '2-digit' as const, month: 'numeric' as const, day: 'numeric' as const };
    const formatDate = () => {
        if (new Date(event.startDate).toLocaleDateString('en-US', { timeZone: 'UTC' }) === new Date(event.endDate).toLocaleDateString('en-US', { timeZone: 'UTC' })) {
            return formatDateForDisplay(event.startDate, opts);
        }
        return formatDateForDisplay(event.startDate, opts) + ' - ' + formatDateForDisplay(event.endDate, opts);
    };

    const statusLabel = getEventStatusLabel(event);
    const publishKind = getEventPublishIconKind(event);
    const fillRate = getEventFillRate(event.playerCap, event.registrationCount);
    const registrationCount = event.registrationCount ?? 0;
    const playerCap = event.playerCap;
    const fillRateTooltip =
        playerCap != null && playerCap > 0
            ? `${Math.round(fillRate)}% filled — ${registrationCount} of ${playerCap} registered`
            : `${registrationCount} registered`;
    const logoSrc = !logoFailed && event.eventCardImageURL ? event.eventCardImageURL : null;

    return (
        <Box sx={{ width: 400 * factor, height: '100%' }}>
            <Paper
                elevation={0}
                sx={{
                    padding: 1.5,
                    width: 400 * factor,
                    minHeight: 200 * factor,
                    borderRadius: '5px',
                    boxShadow: '0px 0px 4px 1px rgba(0,0,0,0.25)',
                    backgroundColor: 'background.paper',
                    position: 'relative',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    boxSizing: 'border-box',
                }}
                onClick={onSelect}
            >
                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: '100%' }}>
                    <Box
                        sx={{
                            width: 100 * factor,
                            height: 80 * factor,
                            flexShrink: 0,
                            overflow: 'hidden',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {logoSrc && (
                            <Box
                                component="img"
                                src={logoSrc}
                                alt=""
                                onError={() => setLogoFailed(true)}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    display: 'block',
                                }}
                            />
                        )}
                    </Box>
                    <Stack spacing={0.25} sx={{ flex: 1, flexShrink: 1, minWidth: 0, alignItems: 'flex-end', textAlign: 'right' }}>
                        <AutoFitText
                            variant="subtitle1"
                            maxFontSize={16 * factor}
                            minFontSize={10}
                            sx={{ fontWeight: 500, lineHeight: 1.2, textAlign: 'right' }}
                        >
                            {event.eventName}
                        </AutoFitText>
                        <AutoFitText
                            variant="body2"
                            maxFontSize={14 * factor}
                            minFontSize={9}
                            sx={{ lineHeight: 1.3, textAlign: 'right' }}
                        >
                            {event.courseName || ''}
                        </AutoFitText>
                        <Typography variant="body2" noWrap sx={{ width: '100%' }}>
                            Date(s) : {formatDate()}
                        </Typography>
                    </Stack>
                </Stack>

                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ width: '100%', px: 0.5 }}>
                    <Stack spacing={0.5} sx={{ width: 110 * factor }}>
                        <Stack direction="row" alignItems="center" sx={{ width: '100%' }}>
                            <Typography variant="body2" sx={{ flex: 1, textAlign: 'right', pr: 0.75 }}>
                                Rounds
                            </Typography>
                            <Typography variant="body2" sx={{ flex: 1, textAlign: 'left', pl: 0.75 }}>
                                {event.totalRounds}
                            </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" sx={{ width: '100%' }}>
                            <Typography variant="body2" sx={{ flex: 1, textAlign: 'right', pr: 0.75 }}>
                                Holes
                            </Typography>
                            <Typography variant="body2" sx={{ flex: 1, textAlign: 'left', pl: 0.75 }}>
                                {event.totalHoles}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Stack spacing={0.75} sx={{ width: 80 * factor }}>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ width: '100%', px: '10px', boxSizing: 'border-box' }}
                        >
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>
                                Field
                            </Typography>
                            <Typography variant="body2" sx={{ textAlign: 'right' }}>
                                {event.playerCap ?? '—'}
                            </Typography>
                        </Stack>
                        <Tooltip title={fillRateTooltip} arrow placement="top">
                            <Box sx={{ width: '100%', display: 'flex' }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={fillRate}
                                    aria-label="Field fill rate"
                                    sx={{
                                        width: '100%',
                                        height: 10,
                                        borderRadius: 5,
                                        backgroundColor: 'sepia.extraLight',
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 5,
                                            backgroundColor: 'primary.light',
                                        },
                                    }}
                                />
                            </Box>
                        </Tooltip>
                    </Stack>
                </Stack>

                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: '100%', mt: 'auto', px: 0.5, gap: 1 }}
                >
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        {event.playType && <SepiaChip label={event.playType} displayOnly />}
                        {statusLabel && <Chip label={statusLabel} size="small" sx={getEventStatusChipSx(statusLabel)} />}
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
                        {event.privacyRegType && (
                            <Typography variant="body2" sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {event.privacyRegType}
                            </Typography>
                        )}
                        <EventPublishStatusIcon kind={publishKind} />
                    </Stack>
                </Stack>
            </Paper>
        </Box>
    );
});

EventCard.displayName = 'EventCard';

interface EventCardSectionProps {
    year: number;
    togglePastEvents: boolean;
    handleSelectEvent: (event: any) => void;
}

export const EventCardSection = ({ year, togglePastEvents, handleSelectEvent }: EventCardSectionProps) => {
    const { appTheme, factor, eventData } = useTBContext();
    const currentYear = new Date().getUTCFullYear();
    const [open, setOpen] = useState(year === currentYear);
    const yearEvents = eventData
        .filter((event: any) => ((togglePastEvents || !isEventPast(event.endDate)) && event.year === year))
        .sort((a: any, b: any) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());

    return (
        <Stack key={year} width={'100%'} justifyContent={'space-evenly'}>
            <Stack direction={'row'} justifyContent={'flex-start'} alignItems={'center'} spacing={2}>
                <Typography variant='subtitle1' sx={{ fontWeight: 500, textAlign: 'left' }}>
                    {year} Season ({yearEvents.length})
                </Typography>
                <IconButton
                    sx={{ width: 30, height: 30 }}
                    onClick={() => setOpen(!open)}
                >
                    {open ?
                        <ArrowDropUp sx={{ color: appTheme.palette.primary.main, fontSize: 30 }} />
                        :
                        <ArrowDropDown sx={{ color: appTheme.palette.primary.main, fontSize: 30 }} />
                    }
                </IconButton>
            </Stack>
            {open && (
                <Stack
                    flex={1}
                    justifyContent={'space-evenly'}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(auto-fill, ${400 * factor}px)`,
                        gridTemplateRows: `max-content`,
                        gap: 3,
                        justifyItems: 'start',
                        alignItems: 'center',
                        overflowY: 'auto',
                        padding: 1,
                    }}
                >
                    {yearEvents.map((event: any, index: number) => (
                            <EventCard
                                key={String(event._id ?? `evt-${year}-${index}`)}
                                event={event}
                                factor={factor}
                                onSelect={() => handleSelectEvent(event)}
                            />
                        ))}
                </Stack>
            )}
        </Stack>
    );
};
